<template>
    <div v-if="showDialog" class="learn-more-dialog-overlay" @click.self="closeModal">
        <div class="learn-more-dialog">
            <button class="learn-more-close-btn" @click="closeModal">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>
            <iframe :src="modalUrl" class="learn-more-iframe" @load="applyThemeToIframe"></iframe>
        </div>
    </div>
</template>

<script>
export default {
    name: 'StoreLearnMoreModal',
    props: {
        products: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            showDialog: false,
            modalUrl: ''
        }
    },
    methods: {
        openLearnMore(productId) {
            const product = this.products.find(p => p.id === productId)
            if (product && product.slug) {
                const basePath = product.templateToUse === 2 ? '/store-product-2' : '/store-product'
                this.modalUrl = `${basePath}/${product.slug}?modal=true`
                this.showDialog = true
                document.body.style.overflow = 'hidden'
                document.documentElement.style.overflow = 'hidden'
            }
        },
        closeModal() {
            this.showDialog = false
            this.modalUrl = ''
            document.body.style.overflow = ''
            document.documentElement.style.overflow = ''
        },
        applyThemeToIframe(event) {
            try {
                const currentTheme = document.documentElement.getAttribute('data-theme')
                if (currentTheme) {
                    const iframeDoc = event.target.contentDocument || event.target.contentWindow.document
                    iframeDoc.documentElement.setAttribute('data-theme', currentTheme)
                }
            } catch (e) {
                // Same-origin only, will work since iframe is same domain
            }
        }
    },
    beforeDestroy() {
        if (this.showDialog) {
            document.body.style.overflow = ''
            document.documentElement.style.overflow = ''
        }
    }
}
</script>

<style scoped>
.learn-more-dialog-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    z-index: 99999999;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 24px;
}

.learn-more-dialog {
    position: relative;
    width: 100%;
    max-width: 1100px;
    height: calc(100vh - 80px);
    max-height: 900px;
    background-color: #fff;
    border-radius: 16px;
    overflow: hidden;
    box-shadow:
        0 0 0 1px rgba(0, 0, 0, 0.05),
        0 25px 50px -12px rgba(0, 0, 0, 0.35),
        0 12px 24px -8px rgba(0, 0, 0, 0.2);
}

.learn-more-close-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 44px;
    height: 44px;
    border: none;
    background-color: #fff;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    box-shadow:
        0 2px 8px rgba(0, 0, 0, 0.12),
        0 0 0 1px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
}

.learn-more-close-btn:hover {
    background-color: #f9fafb;
    transform: scale(1.08);
    box-shadow:
        0 4px 12px rgba(0, 0, 0, 0.15),
        0 0 0 1px rgba(0, 0, 0, 0.08);
}

.learn-more-close-btn:active {
    transform: scale(0.95);
}

.learn-more-close-btn svg {
    color: #374151;
    width: 20px;
    height: 20px;
}

.learn-more-iframe {
    width: 100%;
    height: 100%;
    border: none;
    background-color: #fff;
}

/* Tablet */
@media (max-width: 1024px) {
    .learn-more-dialog-overlay {
        padding: 16px;
    }

    .learn-more-dialog {
        max-width: 95vw;
        height: calc(100vh - 60px);
        max-height: none;
    }
}

/* Mobile */
@media (max-width: 768px) {
    .learn-more-dialog-overlay {
        padding: 0;
        align-items: flex-end;
    }

    .learn-more-dialog {
        max-width: 100%;
        width: 100%;
        height: calc(100vh - 40px);
        border-radius: 20px 20px 0 0;
    }

    .learn-more-close-btn {
        top: 12px;
        right: 12px;
        width: 40px;
        height: 40px;
    }

    .learn-more-close-btn svg {
        width: 18px;
        height: 18px;
    }
}
</style>

<style>
/* Dark mode overrides */
[data-theme="dark"] .learn-more-dialog {
    background-color: var(--dash-color-white, #181818);
    box-shadow:
        0 0 0 1px rgba(255, 255, 255, 0.08),
        0 25px 50px -12px rgba(0, 0, 0, 0.6),
        0 12px 24px -8px rgba(0, 0, 0, 0.4);
}

[data-theme="dark"] .learn-more-close-btn {
    background-color: var(--dash-color-gray-100, #242424);
    box-shadow:
        0 2px 8px rgba(0, 0, 0, 0.3),
        0 0 0 1px rgba(255, 255, 255, 0.08);
}

[data-theme="dark"] .learn-more-close-btn:hover {
    background-color: var(--dash-color-gray-200, #2e2e2e);
    box-shadow:
        0 4px 12px rgba(0, 0, 0, 0.4),
        0 0 0 1px rgba(255, 255, 255, 0.1);
}

[data-theme="dark"] .learn-more-close-btn svg {
    color: var(--dash-color-gray-700, #d4d4d4);
}

[data-theme="dark"] .learn-more-iframe {
    background-color: var(--dash-color-white, #181818);
}
</style>
